import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import PageSEO from '@/components/base/PageSEO';
import MovieCard from '@/components/feature/MovieCard';
import { allContent, categoryMap, subCategoryMap } from '@/mocks/content';
import type { Movie } from '@/mocks/content';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSub, setActiveSub] = useState('全部');

  const categoryKey = useMemo(() => {
    const entry = Object.entries(categoryMap).find(([, v]) => v.slug === slug);
    return entry ? entry[0] : 'movie';
  }, [slug]);

  const categoryLabel = categoryMap[categoryKey]?.label || '电影';
  const subCategories = subCategoryMap[categoryKey] || ['全部'];

  const filteredContent = useMemo(() => {
    let items = allContent.filter((m: Movie) => m.category === categoryKey);
    if (activeSub !== '全部') {
      items = items.filter((m: Movie) => m.subCategory === activeSub);
    }
    return items;
  }, [categoryKey, activeSub]);

  useEffect(() => {
    setActiveSub('全部');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const catTitle = `${categoryLabel} - 色影视高清免费${categoryLabel}在线观看`;
  const catDesc = `色影视提供海量高清免费${categoryLabel}资源，涵盖${subCategories.slice(1).join('、')}等多种类型，超清画质不缓冲流畅播放，每日更新最新${categoryLabel}。`;

  const catJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: catTitle,
    description: catDesc,
    url: `https://www.seyingshi.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '色影视',
      url: 'https://www.seyingshi.com/',
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.seyingshi.com/' },
      { '@type': 'ListItem', position: 2, name: categoryLabel, item: `https://www.seyingshi.com/category/${slug}` },
    ],
  };

  return (
    <>
      <PageSEO
        title={catTitle}
        description={catDesc}
        keywords={`高清免费${categoryLabel},${categoryLabel}在线观看,${categoryLabel}大全,超清${categoryLabel}免费,色影视`}
        canonicalUrl={`https://www.seyingshi.com/category/${slug}`}
        jsonLd={[catJsonLd, breadcrumbLd]}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb />

          {/* Category Header */}
          <section className="w-full max-w-[1400px] mx-auto px-4 md:px-6 py-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              高清免费{categoryLabel}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              色影视为您精选海量高清{categoryLabel}资源，超清画质流畅播放
            </p>
          </section>

          {/* Sub-category tabs */}
          <section className="w-full max-w-[1400px] mx-auto px-4 md:px-6 pb-4">
            <div className="flex items-center gap-2 flex-wrap">
              {subCategories.map((sub) => (
                <button
                  key={sub}
                  onClick={() => {
                    setActiveSub(sub);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeSub === sub
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {sub}
                </button>
              ))}
            </div>
          </section>

          {/* Content grid */}
          <section className="w-full max-w-[1400px] mx-auto px-4 md:px-6 pb-12">
            {filteredContent.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="true">
                {filteredContent.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400" />
                </div>
                <p className="text-foreground-500">暂无相关内容</p>
              </div>
            )}
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}